import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";

/**
 * Renders the canonical NPC card PNG (same as the printed plastic card and
 * the admin modal). Fetches `/api/memberships/by-id/{id}/card.png` as a blob
 * and shows it as an <img>. Pass `memberId` to render a specific
 * sub-member's card; omit it for the primary.
 */
export default function CanonicalCardImage({ membershipId, memberId, side = "front", className = "", testId }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!membershipId) return;
    let cancelled = false;
    let createdUrl = null;
    setLoading(true);
    setError(null);
    const endpoint = side === "back" ? "card-back.png" : "card.png";
    const params = memberId ? { member_id: memberId } : undefined;
    (async () => {
      try {
        const res = await api.get(`/memberships/by-id/${membershipId}/${endpoint}`, {
          responseType: "blob",
          params,
        });
        if (cancelled) return;
        createdUrl = URL.createObjectURL(res.data);
        setBlobUrl(createdUrl);
      } catch (e) {
        if (!cancelled) setError(apiErr(e, "Failed to load card"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [membershipId, memberId, side]);

  return (
    <div
      className={`relative w-full max-w-[440px] aspect-[1006/640] rounded-2xl overflow-hidden bg-[#F4F7FB] border border-[#E1E8F0] shadow-sm ${className}`}
      data-testid={testId}
    >
      {loading && (
        <div className="absolute inset-0 grid place-items-center text-xs text-[#5C6B7A]">
          Generating card…
        </div>
      )}
      {error && (
        <div className="absolute inset-0 grid place-items-center text-xs text-[#E63946] px-4 text-center">
          {error}
        </div>
      )}
      {!loading && !error && blobUrl && (
        <img
          src={blobUrl}
          alt={side === "back" ? "NPC Membership Card (back)" : "NPC Membership Card"}
          className="w-full h-full object-cover block"
        />
      )}
    </div>
  );
}
